/**
 * The real `window.print`, kept reachable after the app suppresses it.
 *
 * ## Why this module exists
 *
 * `index.js` replaces `window.print` with a no-op so a stray Ctrl+P does not
 * print the whole app chrome. That is still wanted — but it made printing
 * IMPOSSIBLE ON WINDOWS, because of how the platforms differ underneath:
 *
 *   macOS   wry → WKWebView `printOperationWithPrintInfo:`  — native, no JS
 *   Linux   wry → webkit2gtk `PrintOperation::run_dialog`   — native, no JS
 *   Windows wry → `eval("window.print()")`                  — RUNS IN THIS PAGE
 *
 * (wry 0.55.1, `src/webview2/mod.rs:1712` — its Windows `print()` really is one
 * line of `eval`.)
 *
 * So on Windows the Rust `webview_print` command hands the job straight back to
 * the page, where it hit our own no-op and vanished. `webview.print()` returns
 * `Ok(())` either way, so nothing surfaced — same silent shape as the WKWebView
 * bug this feature was written to fix, one platform over.
 *
 * Capturing the function before it is replaced makes the suppression a
 * suppression of STRAY prints rather than of printing.
 *
 * ## Ordering
 *
 * This module must evaluate before `window.print` is replaced. It does:
 * `index.js` imports it, and ES module bodies run before the importer's. Moving
 * the capture into `index.js` itself would work too; keeping it here is what
 * lets the print path import it without depending on load order.
 */

const captured =
  typeof window !== 'undefined' && typeof window.print === 'function'
    ? window.print.bind(window)
    : null;

/** Replace `window.print` with a no-op. Call once, from the app entry point. */
export function suppressStrayPrint() {
  if (typeof window !== 'undefined') window.print = () => {};
}

/** True when the page is running on Windows. */
export function isWindows() {
  if (typeof navigator === 'undefined') return false;
  const platform = navigator.userAgentData?.platform || navigator.platform || '';
  return /win/i.test(platform) || /Windows/i.test(navigator.userAgent || '');
}

/**
 * Open the browser's own print dialog.
 * @returns {boolean} false when there was never a real `window.print` to call.
 */
export function callNativePrint() {
  if (!captured) return false;
  captured();
  return true;
}
